/**
 * Nápověda na překlep v doméně e-mailu („jan@gmial.com" → „Nemysleli jste
 * jan@gmail.com?").
 *
 * DNS kontrola na backendu takové adresy nechytí — domény s překlepem typu
 * gmial.com nebo seznam.cy existují a poštu přijímají (registrují se právě
 * kvůli překlepům). Proto porovnáváme doménu se známými doménami: běžní
 * poskytovatelé + domény z adresáře firem a opakovaně použité adresy.
 *
 * Jen upozorňuje — uživatel může adresu potvrdit jako správnou.
 */

/** Běžní poskytovatelé v ČR/SK + velké mezinárodní. */
export const COMMON_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com',
  'seznam.cz', 'email.cz', 'post.cz', 'spoluzaci.cz', 'stream.cz',
  'centrum.cz', 'atlas.cz', 'volny.cz', 'tiscali.cz', 'quick.cz', 'iol.cz',
  'o2active.cz', 'cbox.cz',
  'outlook.com', 'outlook.cz', 'hotmail.com', 'hotmail.cz', 'live.com', 'live.cz', 'msn.com',
  'icloud.com', 'me.com', 'mac.com',
  'yahoo.com', 'protonmail.com', 'proton.me', 'gmx.com', 'gmx.de', 'gmx.net', 'aol.com',
  'azet.sk', 'centrum.sk', 'atlas.sk', 'post.sk', 'zoznam.sk', 'pobox.sk',
];

/** Damerau–Levenshtein (s přehozením sousedních znaků: gmial ↔ gmail). */
function editDistance(a: string, b: string): number {
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
}

/**
 * Vrátí opravenou doménu, když `domain` vypadá jako překlep některé známé.
 * Shoda se známou doménou = žádná nápověda.
 */
export function suggestDomain(domain: string, known: Iterable<string>): string | null {
  const dom = domain.trim().toLowerCase();
  if (!dom) return null;
  const set = new Set([...known].map((k) => k.toLowerCase()));
  if (set.has(dom)) return null;
  // Stejný název, jiná koncovka (gmail.cz → gmail.com, seznam.com → seznam.cz)
  // má přednost před nejbližší shodou po znacích (gmail.cz by jinak dal email.cz).
  const name = dom.slice(0, dom.lastIndexOf('.'));
  if (name) {
    for (const k of set) {
      if (k.slice(0, k.lastIndexOf('.')) === name) return k;
    }
  }
  let best: string | null = null;
  let bestDist = Infinity;
  for (const k of set) {
    // Krátké domény (me.com, post.cz) jen o 1 znak, delší až o 2.
    const limit = Math.min(k.length, dom.length) <= 7 ? 1 : 2;
    if (Math.abs(k.length - dom.length) > limit) continue;
    const dist = editDistance(dom, k);
    if (dist > 0 && dist <= limit && dist < bestDist) {
      best = k;
      bestDist = dist;
    }
  }
  return best;
}

export interface EmailTypoHint {
  /** Adresa tak, jak je napsaná (bez „Jméno <…>"). */
  address: string;
  /** Navržená oprava celé adresy. */
  suggestion: string;
}

/** Projde čárkou/středníkem oddělený seznam adres a vrátí nápovědy na překlepy. */
export function findEmailTypos(value: string, known: Iterable<string>): EmailTypoHint[] {
  const knownArr = [...known];
  const out: EmailTypoHint[] = [];
  for (const part of value.split(/[,;]/)) {
    const raw = part.trim();
    if (!raw) continue;
    const address = (raw.match(/<([^>]*)>/)?.[1] ?? raw).trim();
    const at = address.lastIndexOf('@');
    if (at < 1) continue;
    const fixed = suggestDomain(address.slice(at + 1), knownArr);
    if (fixed) out.push({ address, suggestion: address.slice(0, at + 1) + fixed });
  }
  return out;
}

/** Nahradí v hodnotě pole jednu adresu jinou (ostatní adresy a formát beze změny). */
export function replaceEmailInField(value: string, from: string, to: string): string {
  const esc = from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return value.replace(new RegExp(`(^|[\\s,;<])${esc}(?=$|[\\s,;>])`, 'i'), `$1${to}`);
}
